import { useCallback, useSyncExternalStore } from 'react';

import { useTelegram } from './telegram-context';

/**
 * Whether the trader can see the Mini App now (M7 R2, R3; technical spec §4 "when the screen is
 * visible"): the page is not hidden (`document.visibilityState`) and Telegram has not sent
 * `deactivated`. Re-renders on `visibilitychange` and on Telegram's `activated` / `deactivated`.
 *
 * The two sources are combined because neither is enough alone: a minimised Telegram client
 * does not always hide the web view, and a hidden browser tab sends no Telegram event.
 */
export function useAppVisible(): boolean {
  const telegram = useTelegram();
  const subscribe = useCallback(
    (onChange: () => void) => {
      document.addEventListener('visibilitychange', onChange);
      const offTelegram = telegram.onActiveChange(onChange);
      return () => {
        document.removeEventListener('visibilitychange', onChange);
        offTelegram();
      };
    },
    [telegram],
  );
  const read = useCallback(
    () => document.visibilityState !== 'hidden' && telegram.isActive(),
    [telegram],
  );
  return useSyncExternalStore(subscribe, read);
}
